// src/components/TaskChart.js
import React, { useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    RadialLinearScale,
    ArcElement,
    Filler
} from 'chart.js';
import { Bar, Scatter, Radar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    RadialLinearScale,
    ArcElement,
    Filler,
    Title,
    Tooltip,
    Legend
);

const TaskChart = ({ tasks }) => {
    const [chartType, setChartType] = useState('priority');

    // Enhanced chart configurations with premium styling
    const chartTheme = {
        primaryGradient: ['#667eea', '#764ba2'],
        secondaryGradient: ['#f093fb', '#f5576c'],
        tertiaryGradient: ['#4facfe', '#00f2fe'],
        quaternaryGradient: ['#43e97b', '#38f9d7'],
        colors: {
            importance: ['#ff6b6b', '#ee5a52'],
            urgency: ['#feca57', '#ff9ff3'],
            complexity: ['#48dbfb', '#0abde3'],
            priority: ['#667eea', '#764ba2']
        }
    };

    const createGradient = (ctx, colorStart, colorEnd) => {
        const gradient = ctx.createLinearGradient(0, 0, 0, 400);
        gradient.addColorStop(0, colorStart);
        gradient.addColorStop(1, colorEnd);
        return gradient;
    };

    const priorityChartData = {
        labels: tasks.map(task => task.title.length > 15 ? task.title.substring(0, 15) + '...' : task.title),
        datasets: [
            {
                label: 'Priority Score',
                data: tasks.map(task => task.priority_score),
                backgroundColor: function (context) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return null;
                    return createGradient(ctx, '#667eea', '#764ba2');
                },
                borderColor: '#667eea',
                borderWidth: 2,
                borderRadius: 8,
                borderSkipped: false,
                hoverBackgroundColor: function (context) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return null;
                    return createGradient(ctx, '#5a6fd8', '#6c4f9c');
                },
                hoverBorderColor: '#5a6fd8',
                hoverBorderWidth: 3,
            }
        ],
    };

    const dimensionsChartData = {
        labels: tasks.map(task => task.title.length > 15 ? task.title.substring(0, 15) + '...' : task.title),
        datasets: [
            {
                label: 'Importance',
                data: tasks.map(task => task.importance),
                backgroundColor: function (context) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return null;
                    return createGradient(ctx, '#ff6b6b', '#ee5a52');
                },
                borderColor: '#ff6b6b',
                borderWidth: 2,
                borderRadius: 6,
                borderSkipped: false,
            },
            {
                label: 'Urgency',
                data: tasks.map(task => task.urgency),
                backgroundColor: function (context) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return null;
                    return createGradient(ctx, '#feca57', '#ff9ff3');
                },
                borderColor: '#feca57',
                borderWidth: 2,
                borderRadius: 6,
                borderSkipped: false,
            },
            {
                label: 'Complexity',
                data: tasks.map(task => task.complexity),
                backgroundColor: function (context) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) return null;
                    return createGradient(ctx, '#48dbfb', '#0abde3');
                },
                borderColor: '#48dbfb',
                borderWidth: 2,
                borderRadius: 6,
                borderSkipped: false,
            }
        ],
    };

    const complexityTimeData = {
        datasets: [
            {
                label: 'Tasks',
                data: tasks.map(task => ({
                    x: task.complexity,
                    y: task.estimated_hours,
                    r: Math.max(task.priority_score * 3, 8),
                    task: task.title,
                    priority: task.priority_score
                })),
                backgroundColor: tasks.map(task => {
                    const intensity = task.priority_score / 10;
                    const r = Math.floor(255 * intensity);
                    const g = Math.floor(255 * (1 - intensity * 0.5));
                    const b = Math.floor(255 * (1 - intensity));
                    return `rgba(${r}, ${g}, ${b}, 0.7)`;
                }),
                borderColor: tasks.map(task => {
                    const intensity = task.priority_score / 10;
                    const r = Math.floor(255 * intensity);
                    const g = Math.floor(255 * (1 - intensity * 0.5));
                    const b = Math.floor(255 * (1 - intensity));
                    return `rgb(${r}, ${g}, ${b})`;
                }),
                borderWidth: 2,
                hoverBorderWidth: 3,
                hoverRadius: function (context) {
                    return context.parsed.r + 3;
                }
            }
        ],
    };

    const radarData = {
        labels: ['Importance', 'Urgency', 'Complexity', 'Est. Hours (Normalized)'],
        datasets: tasks.slice(0, 5).map((task, index) => {
            const colors = [
                ['#667eea', '#764ba2'],
                ['#ff6b6b', '#ee5a52'],
                ['#feca57', '#ff9ff3'],
                ['#48dbfb', '#0abde3'],
                ['#43e97b', '#38f9d7']
            ];
            const [color1, color2] = colors[index] || colors[0];

            return {
                label: task.title,
                data: [
                    task.importance,
                    task.urgency,
                    task.complexity,
                    task.estimated_hours / Math.max(...tasks.map(t => t.estimated_hours)) * 10
                ],
                backgroundColor: `${color1}20`,
                borderColor: color1,
                pointBackgroundColor: color1,
                pointBorderColor: '#fff',
                pointHoverBackgroundColor: color2,
                pointHoverBorderColor: color1,
                borderWidth: 3,
                pointRadius: 6,
                pointHoverRadius: 8,
                fill: true,
            }
        })
    };

    const commonOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1500,
            easing: 'easeInOutCubic',
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 20,
                    font: {
                        size: 12,
                        weight: '600',
                        family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                    },
                    color: '#495057'
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#667eea',
                borderWidth: 1,
                cornerRadius: 12,
                padding: 15,
                titleFont: {
                    size: 14,
                    weight: 'bold'
                },
                bodyFont: {
                    size: 12
                },
                callbacks: {
                    title: function (context) {
                        if (chartType === 'complexity') {
                            return context[0].raw.task;
                        }
                        return context[0].label;
                    },
                    afterBody: function (context) {
                        if (chartType === 'complexity') {
                            const point = context[0].raw;
                            return [`Priority Score: ${point.priority.toFixed(1)}`];
                        }
                        return [];
                    }
                }
            }
        }
    };

    const barOptions = {
        ...commonOptions,
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#6c757d',
                    font: {
                        size: 11,
                        weight: '500'
                    },
                    maxRotation: 45,
                    minRotation: 0
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawBorder: false,
                },
                ticks: {
                    color: '#6c757d',
                    font: {
                        size: 11,
                        weight: '500'
                    }
                }
            }
        }
    };

    const scatterOptions = {
        ...commonOptions,
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Complexity Level',
                    color: '#495057',
                    font: {
                        size: 14,
                        weight: '600'
                    }
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawBorder: false,
                },
                ticks: {
                    color: '#6c757d',
                    font: {
                        size: 11,
                        weight: '500'
                    }
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Estimated Hours',
                    color: '#495057',
                    font: {
                        size: 14,
                        weight: '600'
                    }
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawBorder: false,
                },
                ticks: {
                    color: '#6c757d',
                    font: {
                        size: 11,
                        weight: '500'
                    }
                }
            }
        }
    };

    const radarOptions = {
        ...commonOptions,
        scales: {
            r: {
                beginAtZero: true,
                max: 10,
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                },
                angleLines: {
                    color: 'rgba(0, 0, 0, 0.1)',
                },
                pointLabels: {
                    color: '#495057',
                    font: {
                        size: 12,
                        weight: '600'
                    }
                },
                ticks: {
                    display: false
                }
            }
        }
    };

    const chartConfigs = {
        priority: { data: priorityChartData, options: barOptions },
        dimensions: { data: dimensionsChartData, options: barOptions },
        complexity: { data: complexityTimeData, options: scatterOptions },
        radar: { data: radarData, options: radarOptions }
    };

    const chartMetadata = {
        priority: {
            title: '🏆 Priority Score Analysis',
            description: 'AI-calculated priority scores for each task',
            icon: '📊'
        },
        dimensions: {
            title: '📈 Task Dimensions Breakdown',
            description: 'Compare importance, urgency, and complexity across tasks',
            icon: '🎯'
        },
        complexity: {
            title: '⚡ Complexity vs Time Analysis',
            description: 'Bubble size represents priority score',
            icon: '🔄'
        },
        radar: {
            title: '🎖️ Top 5 Tasks Comparison',
            description: 'Multi-dimensional view of your highest priority tasks',
            icon: '🕸️'
        }
    };

    return (
        <div className="task-chart">
            <div className="chart-header">
                <div className="chart-title-section">
                    <h3>
                        <span className="chart-icon">{chartMetadata[chartType].icon}</span>
                        {chartMetadata[chartType].title}
                    </h3>
                    <p>{chartMetadata[chartType].description}</p>
                </div>

                <div className="chart-stats">
                    <div className="stat-item">
                        <span className="stat-value">{tasks.length}</span>
                        <span className="stat-label">Tasks</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{Math.round(tasks.reduce((sum, task) => sum + task.priority_score, 0) / tasks.length * 10) / 10}</span>
                        <span className="stat-label">Avg Priority</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-value">{tasks.reduce((sum, task) => sum + task.estimated_hours, 0)}h</span>
                        <span className="stat-label">Total Hours</span>
                    </div>
                </div>
            </div>

            <div className="chart-type-selector">
                <button
                    className={`chart-btn ${chartType === 'priority' ? 'active' : ''}`}
                    onClick={() => setChartType('priority')}
                >
                    <span className="btn-icon">🏆</span>
                    <span className="btn-text">Priority Scores</span>
                </button>
                <button
                    className={`chart-btn ${chartType === 'dimensions' ? 'active' : ''}`}
                    onClick={() => setChartType('dimensions')}
                >
                    <span className="btn-icon">📈</span>
                    <span className="btn-text">Task Dimensions</span>
                </button>
                <button
                    className={`chart-btn ${chartType === 'complexity' ? 'active' : ''}`}
                    onClick={() => setChartType('complexity')}
                >
                    <span className="btn-icon">⚡</span>
                    <span className="btn-text">Complexity/Time</span>
                </button>
                <button
                    className={`chart-btn ${chartType === 'radar' ? 'active' : ''}`}
                    onClick={() => setChartType('radar')}
                >
                    <span className="btn-icon">🎖️</span>
                    <span className="btn-text">Top 5 Comparison</span>
                </button>
            </div>

            <div className="chart-container">
                <div className="chart-wrapper">
                    {chartType === 'priority' && (
                        <Bar data={chartConfigs.priority.data} options={chartConfigs.priority.options} />
                    )}
                    {chartType === 'dimensions' && (
                        <Bar data={chartConfigs.dimensions.data} options={chartConfigs.dimensions.options} />
                    )}
                    {chartType === 'complexity' && (
                        <Scatter data={chartConfigs.complexity.data} options={chartConfigs.complexity.options} />
                    )}
                    {chartType === 'radar' && (
                        <Radar data={chartConfigs.radar.data} options={chartConfigs.radar.options} />
                    )}
                </div>

                <div className="chart-insights">
                    {chartType === 'priority' && (
                        <div className="insight-card">
                            <h4>📈 Key Insights</h4>
                            <ul>
                                <li>Highest priority: <strong>{tasks.reduce((max, task) => task.priority_score > max.priority_score ? task : max, tasks[0])?.title}</strong></li>
                                <li>Priority range: <strong>{Math.min(...tasks.map(t => t.priority_score)).toFixed(1)} - {Math.max(...tasks.map(t => t.priority_score)).toFixed(1)}</strong></li>
                                <li>Tasks above average: <strong>{tasks.filter(t => t.priority_score > tasks.reduce((sum, task) => sum + task.priority_score, 0) / tasks.length).length}</strong></li>
                            </ul>
                        </div>
                    )}

                    {chartType === 'dimensions' && (
                        <div className="insight-card">
                            <h4>🎯 Dimension Analysis</h4>
                            <ul>
                                <li>Most important: <strong>{tasks.reduce((max, task) => task.importance > max.importance ? task : max, tasks[0])?.title}</strong></li>
                                <li>Most urgent: <strong>{tasks.reduce((max, task) => task.urgency > max.urgency ? task : max, tasks[0])?.title}</strong></li>
                                <li>Most complex: <strong>{tasks.reduce((max, task) => task.complexity > max.complexity ? task : max, tasks[0])?.title}</strong></li>
                            </ul>
                        </div>
                    )}

                    {chartType === 'complexity' && (
                        <div className="insight-card">
                            <h4>⚡ Effort vs Complexity</h4>
                            <ul>
                                <li>Quick wins (low complexity, high priority): <strong>{tasks.filter(t => t.complexity <= 4 && t.priority_score >= 6).length}</strong></li>
                                <li>Major projects (high complexity, high hours): <strong>{tasks.filter(t => t.complexity >= 7 && t.estimated_hours >= 8).length}</strong></li>
                                <li>Average complexity: <strong>{(tasks.reduce((sum, task) => sum + task.complexity, 0) / tasks.length).toFixed(1)}/10</strong></li>
                            </ul>
                        </div>
                    )}

                    {chartType === 'radar' && (
                        <div className="insight-card">
                            <h4>🏅 Top Tasks Overview</h4>
                            <ul>
                                <li>Showing top <strong>{Math.min(5, tasks.length)}</strong> priority tasks</li>
                                <li>Most balanced task: <strong>{tasks.slice(0, 5).reduce((min, task) => {
                                    const variance = [task.importance, task.urgency, task.complexity].reduce((sum, val, _, arr) => {
                                        const mean = arr.reduce((s, v) => s + v, 0) / arr.length;
                                        return sum + Math.pow(val - mean, 2);
                                    }, 0) / 3;
                                    return variance < (min.variance || Infinity) ? { ...task, variance } : min;
                                }, {}).title}</strong></li>
                                <li>Tasks analyzed: <strong>{Math.min(5, tasks.length)}/5</strong></li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
            /* TaskChart Styles */
            .task-chart {
                width: 100%;
                background: white;
                border-radius: 16px;
                overflow: hidden;
                box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.2);
            }

            /* Chart Header */
            .chart-header {
                padding: 25px 30px;
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                border-bottom: 2px solid #dee2e6;
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 20px;
                position: relative;
                overflow: hidden;
            }

            .chart-header::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.05), transparent);
                animation: shimmer 4s ease-in-out infinite;
            }

            @keyframes shimmer {
                0%, 100% { left: -100%; }
                50% { left: 100%; }
            }

            .chart-title-section {
                flex: 1;
                position: relative;
                z-index: 2;
            }

            .chart-title-section h3 {
                margin: 0 0 8px 0;
                font-size: 22px;
                font-weight: 700;
                color: #495057;
                display: flex;
                align-items: center;
                gap: 12px;
            }

            .chart-icon {
                font-size: 24px;
                animation: bounce 2s ease-in-out infinite;
            }

            @keyframes bounce {
                0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
                40% { transform: translateY(-4px); }
                60% { transform: translateY(-2px); }
            }

            .chart-title-section p {
                margin: 0;
                font-size: 14px;
                color: #6c757d;
                font-weight: 500;
            }

            /* Chart Stats */
            .chart-stats {
                display: flex;
                gap: 20px;
                position: relative;
                z-index: 2;
            }

            .stat-item {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 12px 16px;
                background: white;
                border-radius: 12px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                border: 2px solid transparent;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;
                overflow: hidden;
            }

            .stat-item::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
                transition: left 0.6s;
            }

            .stat-item:hover::before {
                left: 100%;
            }

            .stat-item:hover {
                transform: translateY(-2px);
                box-shadow: 0 8px 25px rgba(102, 126, 234, 0.2);
                border-color: #667eea;
            }

            .stat-value {
                font-size: 20px;
                font-weight: 800;
                color: #667eea;
                line-height: 1;
                margin-bottom: 4px;
                position: relative;
                z-index: 2;
            }

            .stat-label {
                font-size: 11px;
                font-weight: 600;
                color: #6c757d;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                position: relative;
                z-index: 2;
            }

            /* Chart Type Selector */
            .chart-type-selector {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 0;
                background: #f8f9fa;
                border-bottom: 2px solid #dee2e6;
                padding: 0;
            }

            .chart-btn {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 20px 16px;
                border: none;
                background: transparent;
                cursor: pointer;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                border-bottom: 3px solid transparent;
                position: relative;
                overflow: hidden;
                gap: 8px;
                min-height: 80px;
            }

            .chart-btn::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
                transition: left 0.6s;
            }

            .chart-btn:hover::before {
                left: 100%;
            }

            .btn-icon {
                font-size: 24px;
                transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;
                z-index: 2;
            }

            .btn-text {
                font-size: 13px;
                font-weight: 600;
                color: #6c757d;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                text-align: center;
                line-height: 1.2;
                position: relative;
                z-index: 2;
            }

            .chart-btn:hover {
                background: rgba(102, 126, 234, 0.1);
                transform: translateY(-2px);
            }

            .chart-btn:hover .btn-icon {
                transform: scale(1.1) rotate(5deg);
            }

            .chart-btn.active {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                border-bottom-color: #ffd700;
                box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
            }

            .chart-btn.active .btn-text {
                color: white;
                font-weight: 700;
            }

            .chart-btn.active .btn-icon {
                transform: scale(1.15);
                filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
            }

            /* Chart Container */
            .chart-container {
                display: grid;
                grid-template-columns: 1fr 300px;
                gap: 0;
                min-height: 500px;
            }

            .chart-wrapper {
                padding: 30px;
                position: relative;
                background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
                height: 500px;
            }

            .chart-wrapper::before {
                content: '';
                position: absolute;
                top: 20px;
                left: 20px;
                right: 20px;
                bottom: 20px;
                border: 2px dashed rgba(102, 126, 234, 0.1);
                border-radius: 12px;
                pointer-events: none;
            }

            /* Chart Insights */
            .chart-insights {
                background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
                border-left: 2px solid #dee2e6;
                padding: 30px 25px;
                overflow-y: auto;
            }

            .insight-card {
                background: white;
                border-radius: 12px;
                padding: 20px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                border: 1px solid rgba(255, 255, 255, 0.5);
                position: relative;
                overflow: hidden;
            }

            .insight-card::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 4px;
                height: 100%;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            }

            .insight-card h4 {
                margin: 0 0 15px 0;
                font-size: 16px;
                font-weight: 700;
                color: #495057;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .insight-card ul {
                margin: 0;
                padding: 0;
                list-style: none;
            }

            .insight-card li {
                padding: 8px 0;
                font-size: 13px;
                color: #6c757d;
                border-bottom: 1px solid #f1f3f4;
                position: relative;
                padding-left: 20px;
            }

            .insight-card li:last-child {
                border-bottom: none;
            }

            .insight-card li::before {
                content: '📌';
                position: absolute;
                left: 0;
                top: 8px;
                font-size: 10px;
            }

            .insight-card li strong {
                color: #495057;
                font-weight: 600;
            }

            /* Loading Animation */
            @keyframes fadeInScale {
                from {
                    opacity: 0;
                    transform: scale(0.95);
                }
                to {
                    opacity: 1;
                    transform: scale(1);
                }
            }

            .chart-wrapper > * {
                animation: fadeInScale 0.8s cubic-bezier(0.4, 0, 0.2, 1);
            }

            /* Responsive Design */
            @media (max-width: 1200px) {
                .chart-container {
                    grid-template-columns: 1fr;
                }
                
                .chart-insights {
                    border-left: none;
                    border-top: 2px solid #dee2e6;
                    max-height: 300px;
                }
                
                .chart-stats {
                    gap: 15px;
                }
                
                .stat-item {
                    padding: 10px 12px;
                }
                
                .stat-value {
                    font-size: 18px;
                }
            }

            @media (max-width: 768px) {
                .chart-header {
                    flex-direction: column;
                    align-items: stretch;
                    gap: 20px;
                    padding: 20px 20px;
                }
                
                .chart-stats {
                    justify-content: center;
                    flex-wrap: wrap;
                    gap: 12px;
                }
                
                .stat-item {
                    flex: 1;
                    min-width: 80px;
                    padding: 12px 10px;
                }
                
                .chart-title-section h3 {
                    font-size: 18px;
                }
                
                .chart-type-selector {
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1px;
                }
                
                .chart-btn {
                    padding: 15px 12px;
                    min-height: 70px;
                }
                
                .btn-icon {
                    font-size: 20px;
                }
                
                .btn-text {
                    font-size: 11px;
                }
                
                .chart-wrapper {
                    padding: 20px;
                    height: 400px;
                }
                
                .chart-insights {
                    padding: 20px;
                }
                
                .insight-card {
                    padding: 15px;
                }
                
                .insight-card h4 {
                    font-size: 14px;
                }
                
                .insight-card li {
                    font-size: 12px;
                    padding: 6px 0;
                    padding-left: 18px;
                }
            }

            @media (max-width: 480px) {
                .chart-header {
                    padding: 15px;
                }
                
                .chart-title-section h3 {
                    font-size: 16px;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 8px;
                }
                
                .chart-icon {
                    font-size: 20px;
                }
                
                .chart-title-section p {
                    font-size: 12px;
                }
                
                .chart-stats {
                    gap: 8px;
                }
                
                .stat-item {
                    padding: 8px 6px;
                    min-width: 60px;
                }
                
                .stat-value {
                    font-size: 16px;
                }
                
                .stat-label {
                    font-size: 9px;
                }
                
                .chart-type-selector {
                    grid-template-columns: 1fr;
                }
                
                .chart-btn {
                    padding: 12px 10px;
                    min-height: 60px;
                    flex-direction: row;
                    gap: 10px;
                }
                
                .btn-icon {
                    font-size: 18px;
                }
                
                .btn-text {
                    font-size: 10px;
                    text-align: left;
                }
                
                .chart-wrapper {
                    padding: 15px;
                    height: 350px;
                }
                
                .chart-insights {
                    padding: 15px;
                }
                
                .insight-card {
                    padding: 12px;
                }
                
                .insight-card h4 {
                    font-size: 13px;
                    margin-bottom: 12px;
                }
                
                .insight-card li {
                    font-size: 11px;
                    padding: 5px 0;
                    padding-left: 16px;
                }
                
                .insight-card li::before {
                    font-size: 8px;
                    top: 5px;
                }
            }

            /* Chart Animation Delays */
            .chart-btn:nth-child(1) { animation-delay: 0.1s; }
            .chart-btn:nth-child(2) { animation-delay: 0.2s; }
            .chart-btn:nth-child(3) { animation-delay: 0.3s; }
            .chart-btn:nth-child(4) { animation-delay: 0.4s; }

            /* Hover Effects for Better UX */
            .chart-header:hover {
                transform: translateY(-1px);
            }

            .insight-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
            }

            /* Focus States for Accessibility */
            .chart-btn:focus {
                outline: 2px solid #667eea;
                outline-offset: 2px;
            }

            .chart-btn:focus:not(:focus-visible) {
                outline: none;
            }

            /* Dark Mode Support */
            @media (prefers-color-scheme: dark) {
                .task-chart {
                    background: #1a1a1a;
                    border-color: rgba(255, 255, 255, 0.1);
                }
                
                .chart-header {
                    background: linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%);
                    border-color: rgba(255, 255, 255, 0.1);
                }
                
                .chart-title-section h3,
                .insight-card h4 {
                    color: #e9ecef;
                }
                
                .chart-title-section p,
                .stat-label,
                .insight-card li {
                    color: #ced4da;
                }
                
                .stat-item,
                .insight-card {
                    background: #2d2d2d;
                    border-color: rgba(255, 255, 255, 0.1);
                }
                
                .chart-type-selector {
                    background: #2d2d2d;
                    border-color: rgba(255, 255, 255, 0.1);
                }
                
                .chart-btn:not(.active) .btn-text {
                    color: #ced4da;
                }
                
                .chart-wrapper {
                    background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
                }
                
                .chart-insights {
                    background: linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%);
                    border-color: rgba(255, 255, 255, 0.1);
                }
            }

            /* Print Styles */
            @media print {
                .task-chart {
                    box-shadow: none;
                    border: 1px solid #ddd;
                    break-inside: avoid;
                }
                
                .chart-header {
                    background: #f8f9fa !important;
                    color: #333 !important;
                    border-color: #ddd !important;
                }
                
                .chart-title-section h3,
                .chart-title-section p {
                    color: #333 !important;
                }
                
                .chart-type-selector {
                    display: none;
                }
                
                .chart-container {
                    grid-template-columns: 1fr;
                }
                
                .chart-insights {
                    border: none;
                    background: white !important;
                }
                
                .stat-item,
                .insight-card {
                    background: white !important;
                    border: 1px solid #ddd !important;
                    box-shadow: none !important;
                }
                
                .chart-icon,
                .btn-icon {
                    display: none;
                }
                
                .chart-wrapper {
                    background: white !important;
                    height: 400px !important;
                }
            }

            /* High Contrast Mode */
            @media (prefers-contrast: high) {
                .task-chart {
                    border: 2px solid #000;
                }
                
                .chart-header {
                    background: #fff !important;
                    border-color: #000 !important;
                }
                
                .chart-title-section h3,
                .insight-card h4 {
                    color: #000 !important;
                }
                
                .chart-btn.active {
                    background: #000 !important;
                    color: #fff !important;
                }
                
                .chart-btn:not(.active) {
                    background: #fff !important;
                    border: 1px solid #000 !important;
                }
                
                .stat-item,
                .insight-card {
                    background: #fff !important;
                    border: 1px solid #000 !important;
                }
            }

            /* Reduced Motion */
            @media (prefers-reduced-motion: reduce) {
                .chart-icon,
                .btn-icon,
                .chart-header::before,
                .chart-btn::before,
                .stat-item::before,
                .insight-card::before {
                    animation: none !important;
                }
                
                .chart-btn,
                .stat-item,
                .insight-card,
                .chart-wrapper > * {
                    transition: none !important;
                    animation: none !important;
                }
            }
            `}</style>
        </div>
    );
};

export default TaskChart;